// @flow
import { connect } from 'react-redux'
import ReplicatedData, { type Props } from './ReplicatedData'
import type { Store } from '../../redux/ducks'
import * as replicaActions from '../../redux/ducks/advertisementReplicas/reducers'
import type { Configs, Crop, ImageConfig, ImageSizeRestrictions } from '../types'
import _ from 'lodash'
import type {
  AdvertisementReplicaPatch,
  AdvertisementReplica
} from '../../redux/ducks/advertisementReplicas/reducers'
import type { ControlVisibility } from '../AdEdit'

type OwnProps = {|
  controlVisibility: ControlVisibility,
  replica: AdvertisementReplica,
  replicaIndex: number,
  configs: Configs,
  columnImageSizeRestrictions: ImageSizeRestrictions,
  multiImageSizeRestrictions: ImageSizeRestrictions,
  action: 'copy' | 'update' | 'create',
  vkBigImageSizeRestrictions: ImageSizeRestrictions,
  vkMultiImageConfig: ImageConfig,
  tmMultiImageConfig: ImageConfig,
  fbMultiImageConfig: ImageConfig,
  glMultiImageConfig: ImageConfig
|}

function mapStateToProps(state: Store, ownProps: OwnProps) {
  const { configs, action, replica } = ownProps

  return {
    action,
    configs,
    isReplica: true,
    allowMultiUpload: false,
    videoStoreId: replica.videoStoreId,
    videoErrors: replica.videoErrors,
    fbThumbnailImageErrors: replica.fbThumbnailImageErrors,
    fbThumbnailImageStoreId: replica.fbThumbnailImageStoreId,
    fbThumbnailImageCrop: replica.fbThumbnailImageCrop,
    multiImageStoreId: replica.multiImageStoreId,
    multiImageErrors: replica.multiImageErrors,
    vkColumnImageCrop: replica.vkMultiImageCrop,
    vkMultiImageCrop: replica.vkMultiImageCrop,
    vkArticleImageCrop: replica.vkArticleImageCrop,
    vkSiteImageCrop: replica.vkSiteImageCrop,
    tmColumnImageCrop: replica.tmMultiImageCrop,
    tmMultiImageCrop: replica.tmMultiImageCrop,
    fbImageCrop: replica.fbImageCrop,
    fbInstagramImageStoreId: replica.fbInstagramImageStoreId,
    fbInstagramImageErrors: replica.fbInstagramImageErrors,
    fbStoriesImageStoreId: replica.fbStoriesImageStoreId,
    fbStoriesImageErrors: replica.fbStoriesImageErrors,
    glImageCrop: replica.glImageCrop,
    fbInstagramImageCrop: replica.fbInstagramImageCrop,
    fbStoriesImageCrop: replica.fbStoriesImageCrop,
    tmSmallImageCrop: replica.tmSmallImageCrop,
    tmSmallImageErrors: replica.tmSmallImageErrors,
    vkBigImageStoreId: replica.vkBigImageStoreId,
    vkBigImageErrors: replica.vkBigImageErrors,
    tmSmallImageStoreId: replica.tmSmallImageStoreId || replica.multiImageStoreId,
    columnImageErrors: replica.columnImageErrors,
    columnImageStoreId: replica.columnImageStoreId
  }
}

function mapDispatchToProps(dispatch: Function, ownProps: OwnProps) {
  const { replicaIndex: index, replica } = ownProps
  const change = (diff: AdvertisementReplicaPatch) => dispatch(replicaActions.patch(index, diff))
  return {
    onMediaRemove: () => dispatch(replicaActions.remove(index)),
    onMultiImageChange: (storeIds: Array<string>) => {
      change({
        multiImageStoreId: storeIds[0],
        multiImageErrors: [],
        vkColumnImageCrop: null,
        vkMultiImageCrop: null,
        vkArticleImageCrop: null,
        vkSiteImageCrop: null,
        tmSmallImageCrop: null,
        glImageCrop: null
      })
      if (!replica.vkBigImageStoreId) {
        change({ vkBigImageErrors: [] })
      }
      if (!replica.tmSmallImageStoreId) {
        change({ tmSmallImageErrors: [], tmSmallImageCrop: null })
      }
      if (!replica.fbInstagramImageStoreId) {
        change({ fbInstagramImageErrors: [], fbInstagramImageCrop: null })
      }
      if (!replica.fbStoriesImageStoreId) {
        change({ fbStoriesImageErrors: [], fbStoriesImageCrop: null })
      }
      change({ fbImageCrop: null })
    },
    onVkBigImageChange: (imageStoreIds: Array<string>) => {
      change({ vkBigImageStoreId: imageStoreIds[0], vkBigImageErrors: [] })
    },
    onVkMultiImageCropChange: (crop: Crop) => {
      change({ vkMultiImageCrop: crop })
    },
    onTmSmallImageChange: (val: string) => {
      change({ tmSmallImageStoreId: val, tmSmallImageErrors: [], tmSmallImageCrop: null })
    },
    onTmMultiImageCropChange: (crop: Crop) => {
      change({ tmMultiImageCrop: crop })
    },
    onTmSmallImageCropChange: (crop: Crop) => {
      change({ tmSmallImageCrop: crop })
    },
    onFbInstagramImageChange: (val: string) => {
      change({
        fbInstagramImageStoreId: val,
        fbInstagramImageErrors: [],
        fbInstagramImageCrop: null
      })
    },
    onFbStoriesImageChange: (val: string) => {
      change({
        fbStoriesImageStoreId: val,
        fbStoriesImageErrors: [],
        fbStoriesImageCrop: null
      })
    },
    onFbThumbnailImageChange: (val: string) => {
      change({
        fbThumbnailImageStoreId: val,
        fbThumbnailImageErrors: [],
        fbThumbnailImageCrop: null
      })
    },
    onFbImageCropChange: (crop: Crop) => {
      change({ fbImageCrop: crop })
    },
    onFbInstagramImageCropChange: (crop: Crop) => {
      change({ fbInstagramImageCrop: crop })
    },
    onFbStoriesImageCropChange: (crop: Crop) => {
      change({ fbStoriesImageCrop: crop })
    },
    onFbThumbnailImageCropChange: (crop: Crop) => {
      change({ fbThumbnailImageCrop: crop })
    },
    onGlImageCropChange: (crop: Crop) => {
      change({ glImageCrop: crop })
    },
    onVideoChange: (val) => change({ videoStoreId: val, videoErrors: [] }),
    onTmColumnImageCropChange: (val: Crop) => change({ tmColumnImageCrop: val }),
    onVkColumnImageCropChange: (val: Crop) => change({ vkColumnImageCrop: val }),
    onVkArticleImageCropChange: (val: Crop) => change({ vkArticleImageCrop: val }),
    onVkSiteImageCropChange: (val: Crop) => change({ vkSiteImageCrop: val }),
    onColumnImageChange: (storeIds: Array<string>) => {
      change({
        columnImageStoreId: storeIds[0],
        columnImageErrors: [],
        vkColumnImageCrop: null,
        tmColumnImageCrop: null
      })
    }
  }
}

const AdEditReplicaContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(ReplicatedData)

export default AdEditReplicaContainer
